import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { useCookieConsent } from './CookieConsentProvider';

const CookieBanner = () => {
  const { consent, bannerVisible, saveConsent, acceptAll, declineAll } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({
    analytics: consent?.analytics ?? false,
    marketing: consent?.marketing ?? false,
  });

  if (!bannerVisible) return null;

  const handleSavePreferences = () => {
    saveConsent({ essential: true, analytics: prefs.analytics, marketing: prefs.marketing });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
        data-testid="cookie-banner"
      >
        <div className="max-w-3xl mx-auto bg-equipr-dark border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
          {/* Main banner */}
          <div className="p-5 md:p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-equipr-yellow/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Shield className="h-5 w-5 text-equipr-yellow" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-base mb-1">
                  We value your privacy
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  We use cookies to improve your experience, analyse site traffic, and support our marketing efforts.
                  You can choose which categories to allow.{' '}
                  <Link to="/legal/privacy" className="text-equipr-yellow hover:underline">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-3 mt-5">
              <button
                onClick={acceptAll}
                className="px-5 py-2.5 bg-equipr-yellow text-equipr-dark text-sm font-bold rounded-lg hover:bg-equipr-yellow/90 transition-colors"
                data-testid="cookie-accept-all"
              >
                Accept All
              </button>
              <button
                onClick={declineAll}
                className="px-5 py-2.5 bg-white/5 text-white/70 text-sm font-medium rounded-lg border border-white/10 hover:bg-white/10 hover:text-white transition-colors"
                data-testid="cookie-decline-all"
              >
                Essential Only
              </button>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1.5 px-4 py-2.5 text-white/50 text-sm font-medium hover:text-white/70 transition-colors ml-auto"
                data-testid="cookie-manage-preferences"
              >
                Manage Preferences
                {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Category details */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="border-t border-white/5 px-5 md:px-6 py-5 space-y-4" data-testid="cookie-preferences-panel">
                  {/* Essential */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium">Essential</p>
                      <p className="text-white/40 text-xs mt-0.5">Required for the website to function. Cannot be disabled.</p>
                    </div>
                    <div className="px-3 py-1 bg-white/5 text-white/40 text-xs font-medium rounded-full">
                      Always On
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium">Analytics</p>
                      <p className="text-white/40 text-xs mt-0.5">Google Analytics — helps us understand how visitors use the site.</p>
                    </div>
                    <button
                      onClick={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        prefs.analytics ? 'bg-equipr-yellow' : 'bg-white/10'
                      }`}
                      data-testid="cookie-toggle-analytics"
                    >
                      <div
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          prefs.analytics ? 'translate-x-[22px]' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium">Marketing</p>
                      <p className="text-white/40 text-xs mt-0.5">HubSpot — tracks form interactions and supports CRM integrations.</p>
                    </div>
                    <button
                      onClick={() => setPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        prefs.marketing ? 'bg-equipr-yellow' : 'bg-white/10'
                      }`}
                      data-testid="cookie-toggle-marketing"
                    >
                      <div
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          prefs.marketing ? 'translate-x-[22px]' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Save preferences */}
                  <div className="pt-2">
                    <button
                      onClick={handleSavePreferences}
                      className="px-5 py-2.5 bg-equipr-yellow text-equipr-dark text-sm font-bold rounded-lg hover:bg-equipr-yellow/90 transition-colors"
                      data-testid="cookie-save-preferences"
                    >
                      Save Preferences
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;
